import { useState, type FC } from "react";
import { Heart, Activity } from "lucide-react";
import BrandLogo from "../../components/BrandLogo";
import { Button } from "../../components/ui/button";
import { gluceraAssessmentCta } from "../../utils/gluceraTypography";
import { Card } from "../../components/ui/card";
import RadioDot from "../../components/common/RadioDot";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { requestNativeGoogleHealthData } from "../../utils/nativeHealthBridge";

type HealthConnectId = "apple_health" | "google_health";

interface DeviceOption {
  id: HealthConnectId;
  name: string;
  description: string;
  icon: FC<{ className?: string }>;
  iconWrapClassName: string;
  iconClassName: string;
}

const HEALTH_PROVIDER_KEY = "glucera_preferred_health_provider";

const deviceOptions: DeviceOption[] = [
  {
    id: "apple_health",
    name: "Apple Health",
    description: "Connect",
    icon: Heart,
    iconWrapClassName: "bg-gradient-to-br from-pink-500 to-rose-600",
    iconClassName: "h-6 w-6 text-white",
  },
  {
    id: "google_health",
    name: "Google Health Connect",
    description: "Connect",
    icon: Activity,
    iconWrapClassName: "bg-gradient-to-br from-blue-500 to-emerald-500",
    iconClassName: "h-6 w-6 text-white",
  },
];

const AppDevicesPage: FC = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<HealthConnectId | "">("");
  const [isConnecting, setIsConnecting] = useState(false);

  const handleNext = async () => {
    if (!selected) return;

    setIsConnecting(true);
    try {
      localStorage.setItem(HEALTH_PROVIDER_KEY, selected);

      if (selected === "google_health") {
        await requestNativeGoogleHealthData();
      }

      navigate("/glucera" + GluceraNavigationRoutes.HOMEPAGE);
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        <div className="space-y-4 p-4">
          <BrandLogo />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="mb-8 space-y-2 text-center">
            <h1 className="text-large-title font-bold text-foreground sm:text-4xl">
              App & Devices
            </h1>
            <p className="text-title3 text-muted-foreground">
              Link Your Favorite App Or Device
            </p>
          </div>

          <div className="space-y-3">
            {deviceOptions.map(device => {
              const Icon = device.icon;
              const isSelected = selected === device.id;

              return (
                <Card
                  key={device.id}
                  onClick={() => setSelected(device.id)}
                  className={`cursor-pointer p-4 transition-all hover:shadow-md ${
                    isSelected
                      ? "border border-primary bg-primary/5"
                      : "border border-border hover:border-primary/50"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${device.iconWrapClassName}`}
                    >
                      <Icon className={device.iconClassName} strokeWidth={2} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-title3 font-medium text-foreground">
                        {device.name}
                      </p>
                      <p className="text-caption1 text-muted-foreground">
                        {device.description}
                      </p>
                    </div>
                    <RadioDot selected={isSelected} />
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        <div className="pb-safe border-t p-4">
          <Button
            onClick={() => void handleNext()}
            disabled={!selected || isConnecting}
            className={gluceraAssessmentCta}
          >
            {isConnecting
              ? "Connecting..."
              : selected
                ? "Next"
                : "Select App Or Device"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppDevicesPage;
